import React from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  IconButton,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";

// Styling intentionally mirrors DeleteProductDialog / PauseProductDialog
// so the three product-related dialogs feel like one family. Only the
// primary button color is different — uses the brand green
// (#248B70) instead of the destructive red, because this is a
// nudge-to-settings, not a confirm-destructive action.
const styles = {
  dialogPaper: {
    borderRadius: "12px",
    maxWidth: "556px",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    p: "14px 24px",
    borderBottom: "1px solid #EAEAEA",
  },
  title: {
    fontWeight: "600",
    color: "rgba(47, 43, 61, 0.9)",
    lineHeight: "28px",
    fontSize: "18px",
  },
  closeIcon: {
    color: "rgba(62, 63, 76, 1)",
    fontSize: "24px",
  },
  dialogContent: {
    p: "24px",
  },
  description: {
    fontSize: "15px",
    color: "rgba(47, 43, 61, 0.9)",
    lineHeight: "22px",
  },
  dialogActions: {
    justifyContent: "flex-end",
    gap: "10px",
    flexDirection: { xs: "column", sm: "row" },
    p: "12px 24px",
  },
  primaryButton: {
    ml: "0px !important",
    width: { xs: "100%", sm: "auto" },
    textTransform: "none",
    color: "rgba(255, 255, 255, 1)",
    fontWeight: 500,
    fontSize: "15px",
    borderRadius: "8px",
    px: "16px",
    py: "6px",
    backgroundColor: "#248B70",
    boxShadow: "none !important",
    "&:hover": {
      backgroundColor: "#1f7b63",
      boxShadow: "none",
    },
  },
};

// `published`: the item went live by being published (a draft got its photo,
// or a paused item was unpaused), not created, so the title says so.
const FulfillmentReminderDialog = ({ open, onClose, onGoToSettings, published = false }) => {
  const { t } = useTranslation("supplier-products");

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        // Backdrop clicks dismiss (matches the X). Escape also dismisses.
        // We intentionally allow these here — the dialog is a friendly
        // nudge, not a confirm-destructive action like delete/pause.
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          onClose();
          return;
        }
        onClose();
      }}
      maxWidth="556px"
      PaperProps={{ sx: styles.dialogPaper }}
    >
      <Box sx={styles.header}>
        <Typography sx={styles.title}>
          {t(published ? "fulfillment_reminder.title_published" : "fulfillment_reminder.title")}
        </Typography>
        <IconButton sx={{ mr: "-10px" }} onClick={onClose}>
          <CloseIcon sx={styles.closeIcon} />
        </IconButton>
      </Box>

      <DialogContent sx={styles.dialogContent}>
        <Typography sx={styles.description}>
          {t("fulfillment_reminder.body")}
        </Typography>
      </DialogContent>

      <DialogActions sx={styles.dialogActions}>
        <Button
          variant="contained"
          onClick={onGoToSettings}
          sx={styles.primaryButton}
        >
          {t("fulfillment_reminder.go_to_settings")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FulfillmentReminderDialog;
